import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ExperienceSection,
  EducationSection,
  CertificateSection,
  SkillSection,
  PaperSection,
  CoreCompetenciesSection,
  SummarySection,
  getPlaceholderClass,
  renderBulletPoints
} from '../common/ResumeComponents';

// Template 11 - 전통적이고 깔끔한 스타일 (1.html 기반)
const Template11 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-11 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header with thick black border */}
      <div className="px-8 pt-8 pb-5 border-b-2 border-black mb-5">
        <h1 className={getPlaceholderClass(hasRealData.name, "text-4xl font-bold mb-2")}>
          {currentData.name}
        </h1>
        <p className={getPlaceholderClass(hasRealData.title, "text-lg text-gray-700")}>
          {currentData.title}
        </p>
      </div>

      <div className="px-8 pb-8">
        {/* Contact Information Table */}
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
            {language === 'kr' ? '인적사항' : 'CONTACT INFORMATION'}
          </h2>
          <table className="w-full border-collapse text-sm">
            <tbody>
              {currentData.phone && (
                <tr>
                  <td className="w-24 font-bold p-2 bg-gray-50 border-b border-gray-200">
                    {language === 'kr' ? '연락처' : 'Phone'}
                  </td>
                  <td className={`p-2 border-b border-gray-200 ${getPlaceholderClass(hasRealData.phone)}`}>
                    {currentData.phone}
                  </td>
                </tr>
              )}
              {currentData.email && (
                <tr>
                  <td className="w-24 font-bold p-2 bg-gray-50 border-b border-gray-200">
                    {language === 'kr' ? '이메일' : 'Email'}
                  </td>
                  <td className={`p-2 border-b border-gray-200 ${getPlaceholderClass(hasRealData.email)}`}>
                    {currentData.email}
                  </td>
                </tr>
              )}
              {currentData.address && (
                <tr>
                  <td className="w-24 font-bold p-2 bg-gray-50 border-b border-gray-200">
                    {language === 'kr' ? '주소' : 'Address'}
                  </td>
                  <td className={`p-2 border-b border-gray-200 ${getPlaceholderClass(hasRealData.address)}`}>
                    {currentData.address}
                  </td>
                </tr>
              )}
              {currentData.socialLinks && currentData.socialLinks.length > 0 && currentData.socialLinks.map((social, index) => (
                <tr key={index}>
                  <td className="w-24 font-bold p-2 bg-gray-50 border-b border-gray-200 capitalize">
                    {social.type || social.platform}
                  </td>
                  <td className="p-2 border-b border-gray-200">
                    <a href={social.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {social.url.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Summary */}
        {currentData.summary && (
          <section className="mb-6">
            <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
              {titles.summary}
            </h2>
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm leading-relaxed"
            />
          </section>
        )}

        {/* Key Strengths */}
        {currentData.keyStrengths && currentData.keyStrengths.length > 0 && (
          <section className="mb-6">
            <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
              {titles.keyStrengths}
            </h2>
            <CoreCompetenciesSection
              keyStrengths={currentData.keyStrengths}
              hasRealData={hasRealData}
              bulletStyle="•"
              className="space-y-2"
              textClassName="text-sm leading-relaxed"
            />
          </section>
        )}

        {/* Experience */}
        {currentData.experiences.length > 0 && (
          <section className="mb-6">
            <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
              {titles.experience}
            </h2>
            {currentData.experiences.map((exp, index) => (
              <div key={index} className="mb-5">
                <h3 className={getPlaceholderClass(hasRealData.experiences, "text-lg font-bold")}>
                  {exp.position}
                </h3>
                <p className={getPlaceholderClass(hasRealData.experiences, "text-sm text-gray-600 mb-2")}>
                  {exp.company} | {exp.startDate} - {exp.endDate}
                </p>
                {exp.overallDescription && (
                  <p className={`text-sm leading-relaxed mb-3 whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                    {exp.overallDescription}
                  </p>
                )}
                
                {exp.projects && exp.projects.length > 0 && (
                  <div className="space-y-3">
                    {exp.projects.map((project, projIndex) => (
                      <div key={projIndex} className="pl-4 border-l-[3px] border-gray-200">
                        <h4 className={getPlaceholderClass(hasRealData.experiences, "text-base font-bold mb-1")}>
                          {project.projectName}
                        </h4>
                        {project.projectDescription && (
                          <p className={`text-sm leading-relaxed whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                            {project.projectDescription}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Education */}
        {currentData.education.length > 0 && (
          <section className="mb-6">
            <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
              {titles.education}
            </h2>
            {currentData.education.map((edu, index) => (
              <div key={index} className="text-sm">
                <p className="mb-1">
                  <strong className={getPlaceholderClass(hasRealData.education, "text-base")}>
                    {edu.degree}
                  </strong>
                  , <span className={getPlaceholderClass(hasRealData.education)}>{edu.school}</span>
                </p>
                <p className={getPlaceholderClass(hasRealData.education, "text-gray-600 mb-1")}>
                  {edu.startDate} - {edu.endDate}
                </p>
                {edu.major && (
                  <p className={getPlaceholderClass(hasRealData.education)}>
                    {edu.major}
                  </p>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Skills */}
        {currentData.skills.length > 0 && (
          <section className="mb-6">
            <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
              {titles.skills}
            </h2>
            <p className={getPlaceholderClass(hasRealData.skills, "text-sm leading-relaxed")}>
              {currentData.skills.join(', ')}
            </p>
          </section>
        )}

        {/* Certificates */}
        {currentData.certificates.length > 0 && (
          <section className="mb-6">
            <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
              {titles.certificates}
            </h2>
            {currentData.certificates.map((cert, index) => (
              <div key={index} className="text-sm mb-3 last:mb-0">
                <p className="mb-1">
                  <strong className={getPlaceholderClass(hasRealData.certificates, "text-base")}>
                    {cert.name}
                  </strong>
                </p>
                <p className={getPlaceholderClass(hasRealData.certificates, "text-gray-600")}>
                  {cert.issuer} | {cert.date}
                </p>
              </div>
            ))}
          </section>
        )}

        {/* Papers */}
        {hasRealData.papers && currentData.papers.length > 0 && (
          <section>
            <h2 className="text-xl font-bold mb-4 pb-2 border-b border-gray-300">
              {titles.papers}
            </h2>
            <PaperSection
              papers={currentData.papers}
              hasRealData={hasRealData}
              getPaperTypeText={getPaperTypeText}
              itemClassName="mb-3"
              titleClassName="text-base font-bold"
              authorsClassName="text-sm text-gray-600"
              journalClassName="text-sm"
            />
          </section>
        )}
      </div>
    </div>
  );
};

export default Template11;